import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiArrowRight,
  FiBookOpen,
  FiCalendar,
  FiCheckCircle,
  FiClipboard,
  FiGlobe,
  FiMic,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import AuthModal from "../pages/AuthModal.jsx";
import logo from "../assets/logo2.png";
import { useAuth } from "../context/AuthContext.jsx";
import {
  fetchMyOrganizerApplication,
  isApproved,
  isSuperadmin,
} from "../organizer/organizerApplication.js";

const HOST_TYPES = [
  { name: "Performances", copy: "Concerts, comedy, theatre and dance.", icon: FiMic },
  { name: "Experiences", copy: "Workshops, tours and immersive moments.", icon: FiActivity },
  { name: "Expositions", copy: "Art, culture, trade and showcase events.", icon: FiBookOpen },
  { name: "Parties", copy: "Celebrations, nightlife and social gatherings.", icon: FiUsers },
  { name: "Sports", copy: "Matches, races, leagues and tournaments.", icon: FiGlobe },
  { name: "Conferences", copy: "Summits, talks and professional meetups.", icon: FiCalendar },
];

const HIGHLIGHTS = [
  {
    icon: FiZap,
    title: "Go live fast",
    copy: "Create your listing in minutes — we review and publish it for you.",
  },
  {
    icon: FiShield,
    title: "Secure payments",
    copy: "Online ticketing with trusted payment processing and instant e-tickets.",
  },
  {
    icon: FiTrendingUp,
    title: "Track everything",
    copy: "Live sales, revenue and attendee analytics on your own dashboard.",
  },
];

const STEPS = [
  {
    icon: FiClipboard,
    title: "Verify your account",
    copy: "Share your organisation, bank and document details once.",
  },
  {
    icon: FiCalendar,
    title: "Create your event",
    copy: "Add venue, tickets, showtimes and images with our guided form.",
  },
  {
    icon: FiCheckCircle,
    title: "Get approved & sell",
    copy: "Our team reviews your event and it goes live on ShowTimeBro.",
  },
];

/**
 * Public List Your Show landing — shown before Account Setup.
 * Login is only required when the user clicks the CTA.
 */
export default function ListYourShowLandingPage() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [authOpen, setAuthOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const goAfterAuth = "/listyourshow/account-setup";

  const handleListYourShow = async () => {
    if (!user?.token) {
      setAuthOpen(true);
      return;
    }

    // Already verified organizers go to their console; others to account setup.
    if (isSuperadmin()) {
      nav("/listyourshow/manage");
      return;
    }

    setChecking(true);
    try {
      const app = await fetchMyOrganizerApplication();
      if (isApproved(app)) {
        nav("/listyourshow/manage");
      } else {
        nav(goAfterAuth);
      }
    } catch {
      nav(goAfterAuth);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    document.title = "List Your Show | ShowTimeBro";
  }, []);

  return (
    <>
      <div className="bg-[#fffdf8]">
        {/* Hero */}
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <img
            src="https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=2000&q=85"
            alt="A live event crowd under atmospheric stage lights"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="flex min-h-[min(88vh,42rem)] max-w-3xl flex-col justify-center py-16 lg:min-h-[36rem] lg:py-20">
              <p className="mb-3 text-[11px] font-black uppercase tracking-[0.28em] text-amber-300">
                ShowTimeBro
              </p>
              <h1 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                List Your Show
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-slate-200 sm:text-lg">
                Reach your audience on ShowTimeBro. Set up your organiser account, create
                your event, and start selling tickets.
              </p>
              <div className="mt-9">
                <button
                  type="button"
                  onClick={handleListYourShow}
                  disabled={checking}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-8 py-3.5 text-sm font-extrabold text-slate-950 shadow-xl shadow-orange-950/40 transition hover:-translate-y-0.5 hover:from-amber-300 hover:to-orange-400 disabled:opacity-70"
                >
                  {checking ? "Please wait…" : "List Your Show"}
                  {!checking && <FiArrowRight />}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-10">
          <div className="grid gap-4 sm:grid-cols-3">
            {HIGHLIGHTS.map(({ icon: Icon, title, copy }) => (
              <div
                key={title}
                className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm"
              >
                <Icon className="text-xl text-amber-600" />
                <div className="mt-2 text-sm font-extrabold text-slate-900">{title}</div>
                <p className="mt-1 text-xs leading-5 text-slate-600">{copy}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Host types */}
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">
              Made for every kind of gathering
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              What can you host?
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {HOST_TYPES.map(({ name, copy, icon: Icon }) => (
              <div
                key={name}
                className="rounded-2xl border border-amber-100 bg-white p-5 text-center shadow-sm"
              >
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-amber-100 text-xl text-amber-800">
                  <Icon />
                </div>
                <h3 className="mt-4 text-sm font-extrabold text-slate-900">{name}</h3>
                <p className="mt-1 hidden text-xs leading-5 text-slate-500 lg:block">{copy}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="bg-slate-950 py-16 text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-400">
                Simple by design
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">How it works</h2>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {STEPS.map(({ icon: Icon, title, copy }, index) => (
                <div
                  key={title}
                  className="relative rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <span className="absolute right-5 top-4 text-5xl font-black text-white/5">
                    {index + 1}
                  </span>
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-xl text-slate-950">
                    <Icon />
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{copy}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <button
                type="button"
                onClick={handleListYourShow}
                disabled={checking}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-8 py-3.5 text-sm font-extrabold text-slate-950 transition hover:from-amber-300 hover:to-orange-400 disabled:opacity-70"
              >
                {checking ? "Please wait…" : "List Your Show"}
                {!checking && <FiArrowRight />}
              </button>
            </div>
          </div>
        </section>

        <div className="border-t border-amber-100/80 bg-white py-6 text-center text-xs text-slate-500">
          Already an organiser?{" "}
          <Link to="/listyourshow/manage" className="font-bold text-amber-800 hover:underline">
            Go to your console
          </Link>
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab="login"
        next={goAfterAuth}
        logoSrc={logo}
        brandName="Show Time Bro"
        headerVariant="logo"
      />
    </>
  );
}
